import { useState } from 'react';
import { Copy, Eye, Plus, Trash2, Undo2, X } from 'lucide-react';
import {
  PERMISSION_KEYS,
  ROLE_LABELS,
  ROLE_LEVEL,
  ROLE_PERMISSIONS,
  VIEW_ONLY_KEYS,
  expandPermissions,
  type PermissionKey,
} from '@supernova/shared';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  useCreateTenantRole,
  useDeleteTenantRole,
  useTenantRoles,
  useUpdateTenantRole,
  type TenantRole,
} from '@/hooks/use-tenant-roles';
import { PermissionGrid } from './permission-grid';

/** Built-in roles, shown read-only. */
const BUILT_IN = ['owner', 'admin', 'manager', 'employee', 'inventory_manager'] as const;
const BUILT_IN_SUMMARY: Record<(typeof BUILT_IN)[number], string> = {
  owner: 'Everything',
  admin: 'Everything except owner-only settings',
  manager: 'Runs the store, no paying out',
  employee: 'Adds and edits the catalogue',
  inventory_manager: 'Catalogue, promotions and orders',
};

/** What a custom role is treated as on the register and in per-location settings. */
const REGISTER_ROLES = ['employee', 'inventory_manager', 'manager', 'admin'] as const;

interface Draft {
  id?: string;
  name: string;
  baseRole: string;
  permissions: PermissionKey[];
}

type Selection = { builtIn: (typeof BUILT_IN)[number] } | { draft: Draft };

const count = (keys: readonly string[]) => expandPermissions(keys).length;

/** Owners and admins see every role's permissions here, and build their tenant's custom roles. */
export function RolesPanel({ onClose }: { onClose: () => void }) {
  const { data, isLoading } = useTenantRoles();
  const roles = data?.data ?? [];
  const createRole = useCreateTenantRole();
  const updateRole = useUpdateTenantRole();
  const deleteRole = useDeleteTenantRole();
  const [selection, setSelection] = useState<Selection | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState<{ text: string; undo?: PermissionKey[] } | null>(null);
  const [deleting, setDeleting] = useState<TenantRole | null>(null);

  const draft = selection && 'draft' in selection ? selection.draft : null;
  const builtIn = selection && 'builtIn' in selection ? selection.builtIn : null;
  const saving = createRole.isPending || updateRole.isPending;

  const open = (next: Selection | null) => {
    setError('');
    setNotice(null);
    setSelection(next);
  };
  const edit = (patch: Partial<Draft>) => draft && setSelection({ draft: { ...draft, ...patch } });
  const openCustom = (role: TenantRole) =>
    open({
      draft: {
        id: role.id,
        name: role.name,
        baseRole: role.baseRole,
        permissions: expandPermissions(role.permissions),
      },
    });

  const fillFrom = (label: string, keys: readonly string[]) => {
    if (!draft) return;
    const previous = draft.permissions;
    edit({ permissions: expandPermissions(keys) });
    setNotice({
      text: keys.length ? `Filled from ${label}. Adjust the boxes below.` : 'All boxes cleared.',
      undo: previous,
    });
  };

  const change = (add: PermissionKey[], remove: PermissionKey[]) => {
    if (!draft) return;
    const next = new Set(draft.permissions);
    add.forEach((k) => next.add(k));
    remove.forEach((k) => next.delete(k));
    edit({ permissions: PERMISSION_KEYS.filter((k) => next.has(k)) });
    setNotice(null);
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft) return;
    setError('');
    if (!draft.name.trim()) {
      setError('Role name is required');
      return;
    }
    const input = { name: draft.name.trim(), baseRole: draft.baseRole, permissions: draft.permissions };
    try {
      if (draft.id) await updateRole.mutateAsync({ id: draft.id, ...input });
      else await createRole.mutateAsync(input);
      setSelection(null);
      setNotice(null);
    } catch (err: any) {
      setError(err?.error?.message || 'Failed to save role');
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setError('');
    try {
      await deleteRole.mutateAsync(deleting.id);
      setSelection(null);
    } catch (err: any) {
      setError(err?.error?.message || 'Failed to delete role');
    }
    setDeleting(null);
  };

  const listButton = (active: boolean, onClick: () => void, title: string, sub: string, key: string) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      aria-current={active ? 'true' : undefined}
      className={`flex min-h-12 w-full flex-col items-start justify-center rounded-lg border px-3 py-1.5 text-left text-sm ${
        active ? 'border-primary/40 bg-primary/10' : 'border-transparent hover:bg-muted'
      }`}
    >
      <span className="font-medium">{title}</span>
      <span className="text-xs text-muted-foreground">{sub}</span>
    </button>
  );

  const editingRole = draft?.id ? roles.find((r) => r.id === draft.id) : undefined;
  const sources = [
    { label: 'View only (every tab)', keys: VIEW_ONLY_KEYS },
    ...BUILT_IN.filter((r) => r !== 'owner').map((r) => ({ label: ROLE_LABELS[r], keys: ROLE_PERMISSIONS[r] })),
    ...roles.filter((r) => r.id !== draft?.id).map((r) => ({ label: r.name, keys: r.permissions })),
  ];

  return (
    <div className="rounded-xl border border-border bg-white p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Roles &amp; permissions</h3>
          <p className="text-sm text-muted-foreground">
            Choose what each role can see and do. The sections follow the sidebar, so a box here is a screen or button
            there.
          </p>
        </div>
        <button onClick={onClose} aria-label="Close roles" className="rounded-md p-1 hover:bg-muted">
          <X className="h-4 w-4" />
        </button>
      </div>

      {error && <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">{error}</div>}

      <div className="flex flex-wrap items-start gap-5">
        <nav aria-label="Roles" className="flex max-w-xs flex-[1_1_220px] flex-col gap-1">
          <span className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Built-in
          </span>
          {BUILT_IN.map((role) =>
            listButton(
              builtIn === role,
              () => open({ builtIn: role }),
              ROLE_LABELS[role],
              BUILT_IN_SUMMARY[role],
              role,
            ),
          )}
          <span className="px-2 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Custom
          </span>
          {!isLoading && roles.length === 0 && (
            <p className="px-2 text-sm text-muted-foreground">No custom roles yet.</p>
          )}
          {roles.map((role) =>
            listButton(
              draft?.id === role.id,
              () => openCustom(role),
              role.name,
              `${count(role.permissions)} permissions · ${role.userCount} ${role.userCount === 1 ? 'user' : 'users'}`,
              role.id,
            ),
          )}
          <button
            type="button"
            onClick={() => open({ draft: { name: '', baseRole: 'employee', permissions: [] } })}
            className="mt-2 inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-dashed border-gray-400 px-4 text-sm font-semibold text-primary hover:bg-muted"
          >
            <Plus className="h-4 w-4" />
            New role
          </button>
          <button
            type="button"
            onClick={() =>
              open({ draft: { name: 'View only', baseRole: 'employee', permissions: [...VIEW_ONLY_KEYS] } })
            }
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-dashed border-gray-400 px-4 text-sm font-semibold text-primary hover:bg-muted"
          >
            <Eye className="h-4 w-4" />
            New view-only role
          </button>
        </nav>

        <div className="min-w-0 flex-[999_1_520px]">
          {!selection && (
            <p className="rounded-lg border border-dashed border-border p-6 text-sm text-muted-foreground">
              Pick a role to see what it can do, or start a new one.
            </p>
          )}

          {builtIn && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-muted/60 px-4 py-3">
                <div>
                  <div className="font-semibold">{ROLE_LABELS[builtIn]}</div>
                  <div className="text-sm text-muted-foreground">
                    Built-in role. It can&apos;t be changed, but you can copy it to start a custom role.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    open({
                      draft: {
                        name: `${ROLE_LABELS[builtIn]} (copy)`,
                        baseRole: builtIn === 'owner' ? 'admin' : builtIn,
                        permissions: [...ROLE_PERMISSIONS[builtIn]],
                      },
                    })
                  }
                  className="inline-flex min-h-10 items-center gap-2 rounded-lg border border-primary px-4 text-sm font-semibold text-primary hover:bg-primary/5"
                >
                  <Copy className="h-4 w-4" />
                  Copy to a new role
                </button>
              </div>
              <PermissionGrid
                key={builtIn}
                isChecked={(key) => ROLE_PERMISSIONS[builtIn].includes(key)}
                isLocked={() => true}
                onChange={() => {}}
                roleLevel={ROLE_LEVEL[builtIn]}
              />
            </div>
          )}

          {draft && (
            <form onSubmit={save} noValidate className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="role-name" className="mb-1 block text-sm font-medium">
                    Role name
                  </label>
                  <input
                    id="role-name"
                    type="text"
                    value={draft.name}
                    maxLength={100}
                    onChange={(e) => edit({ name: e.target.value })}
                    placeholder="e.g. Senior Cashier"
                    className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="role-register" className="mb-1 block text-sm font-medium">
                    On the register, treat as
                  </label>
                  <select
                    id="role-register"
                    value={draft.baseRole}
                    onChange={(e) => edit({ baseRole: e.target.value })}
                    className="h-10 w-full rounded-lg border border-input bg-background px-3 text-sm"
                  >
                    {REGISTER_ROLES.map((role) => (
                      <option key={role} value={role}>
                        {ROLE_LABELS[role]}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Register permissions, report visibility and cost visibility are set per location for each built-in
                    role. This role follows the {ROLE_LABELS[draft.baseRole]} settings there.
                    {draft.baseRole === 'admin' && ' Admin also opens the Owner and Admin only screens below.'}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <span className="block text-sm font-medium">Fill the boxes from</span>
                <div className="flex flex-wrap gap-2">
                  {sources.map((source) => (
                    <button
                      key={source.label}
                      type="button"
                      onClick={() => fillFrom(source.label, source.keys)}
                      className="min-h-9 rounded-full border border-input bg-white px-3.5 text-xs font-medium hover:bg-muted"
                    >
                      {source.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => fillFrom('Nothing', [])}
                    className="min-h-9 rounded-full border border-input bg-white px-3.5 text-xs font-medium hover:bg-muted"
                  >
                    Nothing
                  </button>
                </div>
                {notice && (
                  <div className="flex flex-wrap items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800">
                    {notice.text}
                    {notice.undo && (
                      <button
                        type="button"
                        onClick={() => {
                          edit({ permissions: notice.undo! });
                          setNotice(null);
                        }}
                        className="inline-flex items-center gap-1 font-semibold text-primary"
                      >
                        <Undo2 className="h-3.5 w-3.5" /> Undo
                      </button>
                    )}
                  </div>
                )}
              </div>

              <PermissionGrid
                key={draft.id ?? 'new'}
                isChecked={(key) => draft.permissions.includes(key)}
                isLocked={() => false}
                onChange={change}
                roleLevel={ROLE_LEVEL[draft.baseRole] ?? 0}
              />

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
                <span className="text-sm text-muted-foreground">
                  {draft.permissions.length} permissions ticked.
                  {draft.id && ' Changes reach this role’s users within 15 minutes.'}
                </span>
                <div className="flex flex-wrap gap-2">
                  {editingRole && (
                    <button
                      type="button"
                      onClick={() => setDeleting(editingRole)}
                      disabled={editingRole.userCount > 0}
                      title={editingRole.userCount > 0 ? 'Move its users to another role first' : undefined}
                      className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete role
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => open(null)}
                    className="rounded-lg border border-border px-6 py-2 text-sm font-medium hover:bg-muted"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-lg bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save role'}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!deleting}
        title="Delete role"
        message={`Delete ${deleting?.name}? This can't be undone.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </div>
  );
}
